/**
 * Shared scheduling-time helpers for anything the backend will publish later
 * (social posts and blogs).
 *
 * Timezone model
 * --------------
 * A scheduled post is stored as TWO values:
 *   - `scheduled_time`      — the UTC instant, e.g. "2026-07-20T14:00:00.000Z"
 *   - `scheduled_timezone`  — the IANA zone id the user actually picked in,
 *                             e.g. "America/Chicago"
 *
 * The instant alone is not enough. It says *when* the post fires but not *whose
 * clock* the person had in mind, so an agency in Manila scheduling for a Chicago
 * client could pick "9:00 AM" and silently mean the wrong moment — and a
 * teammate in a third timezone would see a third rendering of the same row.
 * Storing the zone id (never a fixed offset, which breaks across DST) keeps that
 * intent, lets the picker show the client's clock, and lets every view render the
 * same wall-clock the scheduler chose.
 *
 * The default zone comes from the business profile's address (`Customers.timezone`
 * in customer-management, derived from country/state when not explicitly set) and
 * is overridable per post.
 *
 * Legacy callers that pass no timezone keep the old browser-local behaviour, so
 * blogs and anything not yet migrated are unaffected.
 */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Wall-clock format the MUI picker and `datetime-local` inputs both use. */
const WALL_CLOCK_FORMAT = "YYYY-MM-DDTHH:mm";

/**
 * The viewer's own timezone, e.g. "Asia/Manila". Used as the last-resort default
 * and to decide whether a "your time" hint is worth showing at all.
 */
export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** True if `tz` is a zone id this browser can actually format with. */
export const isValidTimezone = (tz?: string | null): boolean => {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

/**
 * Pick the timezone a schedule picker should default to.
 *
 * Order: the business profile's stored/derived zone → the viewer's browser zone.
 * The backend already guarantees a value on every customer response (it derives
 * one from the address for rows that predate the field), so the browser fallback
 * is defence in depth rather than the expected path.
 */
export const resolveScheduleTimezone = (
  customerTimezone?: string | null
): string =>
  isValidTimezone(customerTimezone)
    ? (customerTimezone as string)
    : getBrowserTimezone();

/**
 * Normalize a picker value or ISO string to a UTC ISO-8601 instant.
 *
 * With `tz`, a naive wall-clock ("2026-07-20T09:00") is read as that zone's local
 * time — this is what makes "9:00 AM" mean 9:00 AM for the *client*, not for
 * whoever happens to be scheduling.
 *
 * Without `tz`, a naive value is read as browser-local (the original behaviour).
 * A value that already carries an offset is unambiguous and `tz` is ignored.
 */
export const normalizeScheduledTime = (value: string, tz?: string): string => {
  if (!value) return value;

  if (tz && isValidTimezone(tz) && !hasExplicitOffset(value)) {
    try {
      // dayjs.tz THROWS on unparseable input rather than returning an invalid
      // instance, so .isValid() alone is not enough of a guard here.
      const zoned = dayjs.tz(value, tz);
      if (zoned.isValid()) return zoned.toDate().toISOString();
    } catch {
      /* fall through to browser-local parsing below */
    }
    return value;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toISOString();
};

/** True if the string carries a UTC marker or numeric offset (…Z / …+05:30). */
const hasExplicitOffset = (value: string): boolean =>
  /(?:Z|[+-]\d{2}:?\d{2})$/.test(value.trim());

/**
 * A UTC instant → the naive wall-clock string to put in a picker, expressed in
 * `tz`. The inverse of `normalizeScheduledTime`; use it when populating a picker
 * for an existing post so the user edits the clock they originally chose.
 */
export const utcToZonedWallClock = (iso: string, tz?: string): string => {
  if (!iso) return "";
  const d = dayjs(iso);
  if (!d.isValid()) return "";
  return tz && isValidTimezone(tz)
    ? d.tz(tz).format(WALL_CLOCK_FORMAT)
    : d.format(WALL_CLOCK_FORMAT);
};

/**
 * Human-readable scheduled time rendered IN a specific timezone, with its label,
 * e.g. "20 Jul 2026, 9:00 am CDT".
 *
 * Falls back to the viewer's timezone when the post has no stored zone (legacy
 * rows), which is exactly what those rows have always displayed as.
 */
export const formatInTimezone = (
  iso: string,
  tz?: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const base: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
    ...options,
  };
  if (tz && isValidTimezone(tz)) base.timeZone = tz;
  try {
    return new Intl.DateTimeFormat(undefined, base).format(d);
  } catch {
    return new Intl.DateTimeFormat(undefined, { ...base, timeZone: undefined }).format(d);
  }
};

/**
 * Human-readable scheduled time WITH its timezone, e.g.
 * "20 Jul 2026, 9:00 am IST".
 *
 * The timezone suffix is the point: it tells the user whose clock they picked.
 * Kept as a named export because blogs already use it; it is now
 * `formatInTimezone` with no zone override.
 */
export const formatScheduledForDisplay = (isoOrLocal: string): string =>
  formatInTimezone(isoOrLocal);

/**
 * The "…and this is when it lands on your clock" hint.
 *
 * Returns "" when the post's timezone matches the viewer's, because in that case
 * the hint would just repeat the primary time and add noise.
 */
export const formatViewerTimeHint = (iso: string, tz?: string): string => {
  if (!iso || !tz || !isValidTimezone(tz)) return "";
  const browserTz = getBrowserTimezone();
  if (tz === browserTz) return "";
  const inPostZone = formatInTimezone(iso, tz);
  const inViewerZone = formatInTimezone(iso, browserTz);
  return inPostZone === inViewerZone ? "" : inViewerZone;
};

/**
 * Current UTC offset for a zone, formatted for a picker label: "GMT+08:00".
 * Computed via Intl so it reflects DST on the given date.
 */
export const getTimezoneOffsetLabel = (tz: string, at: Date = new Date()): string => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "longOffset",
    }).formatToParts(at);
    const name = parts.find((p) => p.type === "timeZoneName")?.value;
    // "longOffset" yields "GMT+8" / "GMT+08:00" / bare "GMT" at zero offset.
    if (!name) return "GMT+00:00";
    if (name === "GMT") return "GMT+00:00";
    const m = name.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (!m) return name;
    const [, sign, hh, mm = "00"] = m;
    return `GMT${sign}${hh.padStart(2, "0")}:${mm}`;
  } catch {
    return "GMT+00:00";
  }
};

/**
 * Every IANA zone id this browser knows, for the timezone picker.
 *
 * `Intl.supportedValuesOf` is unavailable in older engines, so fall back to a
 * curated list that covers the markets the platform onboards into. The user can
 * always still be on a zone outside the fallback — that only affects what the
 * dropdown offers, never what a stored value renders as.
 */
export const listTimezones = (): string[] => {
  const anyIntl = Intl as unknown as {
    supportedValuesOf?: (key: string) => string[];
  };
  if (typeof anyIntl.supportedValuesOf === "function") {
    try {
      return anyIntl.supportedValuesOf("timeZone");
    } catch {
      /* fall through to the curated list */
    }
  }
  return FALLBACK_TIMEZONES;
};

const FALLBACK_TIMEZONES = [
  "UTC",
  "America/Anchorage",
  "America/Argentina/Buenos_Aires",
  "America/Bogota",
  "America/Chicago",
  "America/Denver",
  "America/Edmonton",
  "America/Halifax",
  "America/Lima",
  "America/Los_Angeles",
  "America/Mexico_City",
  "America/New_York",
  "America/Phoenix",
  "America/Santiago",
  "America/Sao_Paulo",
  "America/Toronto",
  "America/Vancouver",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Africa/Lagos",
  "Africa/Nairobi",
  "Asia/Bangkok",
  "Asia/Dubai",
  "Asia/Ho_Chi_Minh",
  "Asia/Hong_Kong",
  "Asia/Jakarta",
  "Asia/Kolkata",
  "Asia/Kuala_Lumpur",
  "Asia/Manila",
  "Asia/Riyadh",
  "Asia/Seoul",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Taipei",
  "Asia/Tokyo",
  "Australia/Adelaide",
  "Australia/Brisbane",
  "Australia/Darwin",
  "Australia/Melbourne",
  "Australia/Perth",
  "Australia/Sydney",
  "Europe/Amsterdam",
  "Europe/Berlin",
  "Europe/Brussels",
  "Europe/Copenhagen",
  "Europe/Dublin",
  "Europe/Helsinki",
  "Europe/Lisbon",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Oslo",
  "Europe/Paris",
  "Europe/Rome",
  "Europe/Stockholm",
  "Europe/Vienna",
  "Europe/Warsaw",
  "Europe/Zurich",
  "Pacific/Auckland",
  "Pacific/Honolulu",
];
